// ABOUTME: Transformation schema for TechLog → Rusada oil and fuel updates.
import { TechLogToRusadaOilAndFuelInput } from './validation';

export const techlogOilAndFuelToRusadaSchema = {
  // Rusada flight ID from the current destination oil/fuel record.
  flightId: (input: TechLogToRusadaOilAndFuelInput) =>
    input.rusada?.currentOilAndFuel?.flightId,

  // Unit of measure - null as sandbox has no units configured
  upliftUnitOfMeasureId: () => null,

  // Fuel carried from last journey = fuel before the first uplift.
  fuelCarriedFromLastJourney: (input: TechLogToRusadaOilAndFuelInput) => {
    const firstUplift = input.techlog?.fuelUplifts?.[0];
    if (!firstUplift) return 0;

    const preUpliftFuel =
      typeof firstUplift.aircraftValuePreUplift === 'number' &&
      Number.isFinite(firstUplift.aircraftValuePreUplift)
        ? firstUplift.aircraftValuePreUplift
        : undefined;

    if (preUpliftFuel !== undefined) {
      return preUpliftFuel;
    }

    const plannedDepartureFuel =
      typeof firstUplift.aircraftPlannedDeparture === 'number' &&
      Number.isFinite(firstUplift.aircraftPlannedDeparture)
        ? firstUplift.aircraftPlannedDeparture
        : undefined;

    if (plannedDepartureFuel === undefined) {
      return 0;
    }

    const actualFuelUplift =
      (typeof firstUplift.aircraftActualUplift === 'number' &&
      Number.isFinite(firstUplift.aircraftActualUplift)
        ? firstUplift.aircraftActualUplift
        : undefined) ??
      (typeof firstUplift.aircraftValueActualUplift === 'number' &&
      Number.isFinite(firstUplift.aircraftValueActualUplift)
        ? firstUplift.aircraftValueActualUplift
        : undefined) ??
      0;

    return Math.max(0, plannedDepartureFuel - actualFuelUplift);
  },

  // Planned fuel uplift from the associated TechLog fuel uplift records.
  fuelUpliftPlanned: (input: TechLogToRusadaOilAndFuelInput) => {
    const fuelUplifts = input.techlog?.fuelUplifts;
    if (!fuelUplifts?.length) return 0;

    let sum = 0;

    for (const uplift of fuelUplifts) {
      const plannedUplift =
        typeof uplift.aircraftPlannedUplift === 'number' &&
        Number.isFinite(uplift.aircraftPlannedUplift)
          ? uplift.aircraftPlannedUplift
          : undefined;

      if (plannedUplift !== undefined) {
        sum += plannedUplift;
        continue;
      }

      const preUpliftFuel =
        typeof uplift.aircraftValuePreUplift === 'number' &&
        Number.isFinite(uplift.aircraftValuePreUplift)
          ? uplift.aircraftValuePreUplift
          : undefined;
      const plannedDepartureFuel =
        typeof uplift.aircraftPlannedDeparture === 'number' &&
        Number.isFinite(uplift.aircraftPlannedDeparture)
          ? uplift.aircraftPlannedDeparture
          : undefined;

      if (preUpliftFuel !== undefined && plannedDepartureFuel !== undefined) {
        sum += Math.max(0, plannedDepartureFuel - preUpliftFuel);
      }
    }

    return sum;
  },

  // Actual fuel uplift from the associated TechLog fuel uplift records.
  fuelUpliftActual: (input: TechLogToRusadaOilAndFuelInput) => {
    const fuelUplifts = input.techlog?.fuelUplifts;
    if (!fuelUplifts?.length) return 0;

    let sum = 0;

    for (const uplift of fuelUplifts) {
      const actualFuelUplift =
        (typeof uplift.aircraftActualUplift === 'number' &&
        Number.isFinite(uplift.aircraftActualUplift)
          ? uplift.aircraftActualUplift
          : undefined) ??
        (typeof uplift.aircraftValueActualUplift === 'number' &&
        Number.isFinite(uplift.aircraftValueActualUplift)
          ? uplift.aircraftValueActualUplift
          : undefined) ??
        0;

      sum += actualFuelUplift;
    }

    return sum;
  },

  // Departure fuel = fuel after the last uplift.
  departureFuel: (input: TechLogToRusadaOilAndFuelInput) => {
    const fuelUplifts = input.techlog?.fuelUplifts;
    const lastUplift = fuelUplifts?.length
      ? fuelUplifts[fuelUplifts.length - 1]
      : undefined;
    if (!lastUplift) return 0;

    const postUpliftFuel =
      typeof lastUplift.aircraftValuePostUplift === 'number' &&
      Number.isFinite(lastUplift.aircraftValuePostUplift)
        ? lastUplift.aircraftValuePostUplift
        : undefined;
    if (postUpliftFuel !== undefined) {
      return postUpliftFuel;
    }

    const plannedDepartureFuel =
      typeof lastUplift.aircraftPlannedDeparture === 'number' &&
      Number.isFinite(lastUplift.aircraftPlannedDeparture)
        ? lastUplift.aircraftPlannedDeparture
        : undefined;
    if (plannedDepartureFuel !== undefined) {
      return plannedDepartureFuel;
    }

    const preUpliftFuel =
      typeof lastUplift.aircraftValuePreUplift === 'number' &&
      Number.isFinite(lastUplift.aircraftValuePreUplift)
        ? lastUplift.aircraftValuePreUplift
        : undefined;
    const actualFuelUplift =
      (typeof lastUplift.aircraftActualUplift === 'number' &&
      Number.isFinite(lastUplift.aircraftActualUplift)
        ? lastUplift.aircraftActualUplift
        : undefined) ??
      (typeof lastUplift.aircraftValueActualUplift === 'number' &&
      Number.isFinite(lastUplift.aircraftValueActualUplift)
        ? lastUplift.aircraftValueActualUplift
        : undefined) ??
      0;

    if (preUpliftFuel !== undefined) {
      return preUpliftFuel + actualFuelUplift;
    }

    return actualFuelUplift;
  },

  // Arrival fuel is derived only for flight parents, where sectorActualFuelBurn is available.
  arrivalFuel: (input: TechLogToRusadaOilAndFuelInput) => {
    const fuelUplifts = input.techlog?.fuelUplifts;
    const lastUplift = fuelUplifts?.length
      ? fuelUplifts[fuelUplifts.length - 1]
      : undefined;
    if (!lastUplift) return 0;

    const actualFuelBurn =
      typeof input.techlog?.sectorActualFuelBurn === 'number' &&
      Number.isFinite(input.techlog.sectorActualFuelBurn)
        ? input.techlog.sectorActualFuelBurn
        : undefined;
    if (actualFuelBurn === undefined) return 0;

    const postUpliftFuel =
      typeof lastUplift.aircraftValuePostUplift === 'number' &&
      Number.isFinite(lastUplift.aircraftValuePostUplift)
        ? lastUplift.aircraftValuePostUplift
        : undefined;
    let departureFuel = postUpliftFuel;

    if (departureFuel === undefined) {
      departureFuel =
        typeof lastUplift.aircraftPlannedDeparture === 'number' &&
        Number.isFinite(lastUplift.aircraftPlannedDeparture)
          ? lastUplift.aircraftPlannedDeparture
          : undefined;
    }

    if (departureFuel === undefined) {
      const preUpliftFuel =
        typeof lastUplift.aircraftValuePreUplift === 'number' &&
        Number.isFinite(lastUplift.aircraftValuePreUplift)
          ? lastUplift.aircraftValuePreUplift
          : undefined;
      const actualFuelUplift =
        (typeof lastUplift.aircraftActualUplift === 'number' &&
        Number.isFinite(lastUplift.aircraftActualUplift)
          ? lastUplift.aircraftActualUplift
          : undefined) ??
        (typeof lastUplift.aircraftValueActualUplift === 'number' &&
        Number.isFinite(lastUplift.aircraftValueActualUplift)
          ? lastUplift.aircraftValueActualUplift
          : undefined) ??
        0;

      departureFuel = (preUpliftFuel ?? 0) + actualFuelUplift;
    }

    return Math.max(0, departureFuel - actualFuelBurn);
  },

  // Preserve the current Rusada oil value when syncing fuel-only updates.
  oilUplift: (input: TechLogToRusadaOilAndFuelInput) =>
    input.rusada?.currentOilAndFuel?.oilUplift ?? 0,
};

export default techlogOilAndFuelToRusadaSchema;
